import {DATE_FORMAT, TIME_FORMAT} from "../services/timezone-service";
import {DATETIME_VIEW} from "../components/datetime-v2/datetime-v2";
import TimezoneStore from "../stores/timezone-store";

/**
 * Default fallback formatting parameters.
 * Used when the TIMEZONES feature is disabled or timezone store is unavailable.
 * Exported for use in useDateTimeFormattingParams hook and components.
 */
export const FALLBACK_FORMAT_PARAMS = {
    timeZone: 'UTC',
    dateFormat: DATE_FORMAT.THIRD,
    timeFormat: TIME_FORMAT.AM_PM,
} as const;

/**
 * Convert database date string to UTC Date object.
 *
 * @param dbDateString - Date string from DB (e.g., "2025-01-04 10:30:00" or "2025-01-04T10:30:00Z")
 * @returns Date object with correct internal UTC timestamp
 *
 * @remarks
 * Prefer this method as it returns a Date with correct internal timestamp.
 * Automatically adds 'Z' suffix for strings without timezone indicator.
 */
export function convertSqlDateTimeStringToDate(dbDateString: string): Date {
    if (dbDateString.endsWith('Z')) {
        return new Date(dbDateString);
    }
    const isoString = dbDateString.replace(" ", "T") + "Z";
    return new Date(isoString);
}

/**
 * Extract date and time parts using Intl.DateTimeFormat in specified timezone.
 *
 * @param date - Date object to format
 * @param dateFormat - Merchant date format preference
 * @param timeFormat - Merchant time format preference (12h or 24h)
 * @param timeZone - IANA timezone string (e.g., "America/New_York")
 * @returns Object with formatted date/time parts
 */
function getDateParts(date: Date, dateFormat: DATE_FORMAT, timeFormat: TIME_FORMAT, timeZone: string) {
    const monthFormat = dateFormat === DATE_FORMAT.FIRST || dateFormat === DATE_FORMAT.SECOND ? '2-digit' : 'short';
    const dateParts = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: monthFormat,
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: timeFormat === TIME_FORMAT.TWENTY_FOUR ? 'h23' : 'h12',
        timeZone: timeZone
    }).formatToParts(date);

    const capitalizeFirstLetter = (str?: string) => {
        if (!str) {
            return str;
        }
        return (str + '').replace(/^\w/, (c) => c.toUpperCase());
    };

    const yearPart = dateParts.find(i => i.type === 'year')?.value;
    const monthPart = capitalizeFirstLetter(dateParts.find(i => i.type === 'month')?.value);
    const dayPart = dateParts.find(i => i.type === 'day')?.value;
    const hourPart = dateParts.find(i => i.type === 'hour')?.value;
    const minutePart = dateParts.find(i => i.type === 'minute')?.value;
    const periodPart = dateParts.find(i => i.type === "dayPeriod")?.value ?? '';

    return {yearPart, monthPart, dayPart, hourPart, minutePart, periodPart};
}

/**
 * Props for getFormattedDate function.
 */
export interface IGetFormattedDateProps {
    /** UTC date string to format */
    utcDateString: string;

    /** Date format to use */
    dateFormat: DATE_FORMAT;

    /** Time format to use */
    timeFormat: TIME_FORMAT;

    /** Timezone to convert to */
    timeZone: string;

    /** What parts to display */
    dateTimeView?: DATETIME_VIEW;
}

/**
 * Format date using timezone-aware mode with merchant settings.
 *
 * @param props - Date formatting configuration
 * @returns Formatted date string according to merchant settings
 */
export function getFormattedDate(
    {
        utcDateString,
        timeZone,
        dateTimeView = DATETIME_VIEW.FULL,
        timeFormat,
        dateFormat
    }: IGetFormattedDateProps): string
{

    if (!utcDateString) {
        return '';
    }

    const utcDate = convertSqlDateTimeStringToDate(utcDateString);

    if (isNaN(utcDate.getTime())) {
        return '';
    }

    const {
        yearPart,
        monthPart,
        dayPart,
        hourPart,
        minutePart,
        periodPart
    } = getDateParts(utcDate, dateFormat, timeFormat, timeZone);

    let dateFormatted = '';
    const timeFormatted = `${hourPart}:${minutePart}${periodPart ? ' ' + periodPart : ''}`;
    const showDay = [DATETIME_VIEW.DATE_ONLY, DATETIME_VIEW.FULL].includes(dateTimeView);

    if (dateFormat === DATE_FORMAT.FIRST) {
        if (showDay) {
            dateFormatted = `${dayPart}/${monthPart}/${yearPart}`;
        }
        else {
            dateFormatted = `${monthPart}/${yearPart}`;
        }
    }
    if (dateFormat === DATE_FORMAT.SECOND) {
        if (showDay) {
            dateFormatted = `${monthPart}/${dayPart}/${yearPart}`;
        }
        else {
            dateFormatted = `${monthPart}/${yearPart}`;
        }
    }
    if (dateFormat === DATE_FORMAT.THIRD) {
        if (showDay) {
            dateFormatted = `${dayPart} ${monthPart} ${yearPart}`;
        }
        else {
            dateFormatted = `${monthPart} ${yearPart}`;
        }
    }
    if (dateFormat === DATE_FORMAT.FOURTH) {
        if (showDay) {
            dateFormatted = `${monthPart} ${dayPart}, ${yearPart}`;
        }
        else {
            dateFormatted = `${monthPart}, ${yearPart}`;
        }
    }

    if (dateTimeView === DATETIME_VIEW.FULL) {
        return dateFormatted + ' ' + timeFormatted;
    }
    if (dateTimeView === DATETIME_VIEW.DATE_ONLY || dateTimeView === DATETIME_VIEW.MONTH_YEAR_ONLY) {
        return dateFormatted;
    }
    if (dateTimeView === DATETIME_VIEW.TIME_ONLY) {
        return timeFormatted;
    }

    return '';
}

/**
 * Format date string with automatic fallback to unified DATE_FORMAT.FIRST format.
 * Uses timezone store settings if TIMEZONES feature is enabled and tzStore is available,
 * otherwise falls back to unified DATE_FORMAT.FIRST format.
 * @param tzStore - TimezoneStore instance (can be null/undefined)
 * @param dateString - UTC date string to format (null returns '--')
 * @param dateTimeView - What parts to display (defaults to FULL)
 * @returns Formatted date string (merchant timezone if feature enabled and tzStore available, DATE_FORMAT.FIRST fallback otherwise)
 * @remarks
 * This utility encapsulates the common pattern of formatting dates with fallback.
 * It checks TIMEZONES feature availability and uses tzStore settings if available,
 * otherwise uses a unified DATE_FORMAT.FIRST format for consistency.
 */
export function formatDateTimeWithFallback(
    tzStore: TimezoneStore | null | undefined,
    dateString: string | null,
    dateTimeView: DATETIME_VIEW = DATETIME_VIEW.FULL
): string {
    if (dateString === null) {
        return '--';
    }

    if (tzStore) {
        return getFormattedDate({
            utcDateString: dateString,
            timeZone: tzStore.timezone,
            dateFormat: tzStore.dateFormat,
            timeFormat: tzStore.timeFormat,
            dateTimeView
        });
    }

    return getFormattedDate({
        utcDateString: dateString,
        timeZone: FALLBACK_FORMAT_PARAMS.timeZone,
        dateFormat: FALLBACK_FORMAT_PARAMS.dateFormat,
        timeFormat: FALLBACK_FORMAT_PARAMS.timeFormat,
        dateTimeView
    });
}

/**
 * Calendar date components (no time-of-day), timezone-agnostic.
 */
export interface ICalendarDateParts {
    year: number;
    /** 0-based month, matching Date#getMonth */
    month: number;
    day: number;
}

/**
 * Convert a calendar date interpreted in the given timezone to a UTC ISO string at the
 * specified clock time. Used to build ISO UTC range boundaries (e.g. 00:00:00 / 23:59:59)
 * for date-range filters and the date picker.
 *
 * @param year - Full year
 * @param month - 0-based month (matching Date#getMonth)
 * @param day - Day of month
 * @param hour - Hour of day (0-23)
 * @param minute - Minute (0-59)
 * @param second - Second (0-59)
 * @param timeZone - IANA timezone string (e.g. "America/New_York")
 * @returns ISO UTC string for the given wall-clock time in the timezone
 */
export const dateComponentsToUtcIso = (
    year: number,
    month: number,
    day: number,
    hour: number,
    minute: number,
    second: number,
    timeZone: string,
): string => {
    const monthStr = (month + 1).toString().padStart(2, '0');
    const dayStr = day.toString().padStart(2, '0');
    const hourStr = hour.toString().padStart(2, '0');
    const minStr = minute.toString().padStart(2, '0');
    const secStr = second.toString().padStart(2, '0');

    const dateStr = `${year}-${monthStr}-${dayStr}T${hourStr}:${minStr}:${secStr}`;
    const utcDate = new Date(`${dateStr}Z`);

    const tzFormatter = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });

    const tzFormatted = tzFormatter.format(utcDate);
    const [datePart, timePart] = tzFormatted.split(', ');
    const [tzYear, tzMonth, tzDay] = datePart.split('-').map(Number);
    const [tzHour, tzMin, tzSec] = timePart.split(':').map(Number);

    const desiredUTC = Date.UTC(year, month, day, hour, minute, second);
    const actualUTC = Date.UTC(tzYear, tzMonth - 1, tzDay, tzHour, tzMin, tzSec);
    const offsetMs = desiredUTC - actualUTC;

    return new Date(utcDate.getTime() + offsetMs).toISOString();
};

/**
 * Extract the calendar year/month/day of an ISO UTC instant as seen in the given timezone.
 *
 * @param isoUtcString - ISO UTC date string
 * @param timeZone - IANA timezone string (e.g. "America/New_York")
 * @returns Calendar date parts, or undefined when the input is not a valid date
 */
export const getCalendarDateInTimeZone = (
    isoUtcString: string,
    timeZone: string,
): ICalendarDateParts | undefined => {
    const parsedDate = new Date(isoUtcString);
    if (isNaN(parsedDate.getTime())) {
        return undefined;
    }

    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });

    const parts = formatter.formatToParts(parsedDate);
    const yearString = parts.find((part) => part.type === 'year')?.value;
    const monthString = parts.find((part) => part.type === 'month')?.value;
    const dayString = parts.find((part) => part.type === 'day')?.value;

    if (!yearString || !monthString || !dayString) {
        return undefined;
    }

    return {
        year: parseInt(yearString, 10),
        month: parseInt(monthString, 10) - 1,
        day: parseInt(dayString, 10),
    };
};

const DEFAULT_UI_DATE_FORMAT_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/**
 * Keep digits only and insert `/` so typing stays in MM/DD/YYYY shape (max 8 digits).
 */
export const maskDefaultDatePattern = (raw: string): string => {
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 2) {
        return digits;
    }
    if (digits.length <= 4) {
        return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

/**
 * Parse a MM/DD/YYYY string into calendar parts, or undefined when the string is not a real calendar day.
 */
export const parseDateFormat = (value: string): ICalendarDateParts | undefined => {
    const match = DEFAULT_UI_DATE_FORMAT_PATTERN.exec(value.trim());
    if (!match) {
        return undefined;
    }

    const monthNumber = parseInt(match[1], 10);
    const day = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);

    if (monthNumber < 1 || monthNumber > 12 || day < 1 || day > 31) {
        return undefined;
    }

    const month = monthNumber - 1;
    const probe = new Date(year, month, day);
    if (probe.getFullYear() !== year || probe.getMonth() !== month || probe.getDate() !== day) {
        return undefined;
    }

    return { year, month, day };
};

/**
 * Format calendar parts as MM/DD/YYYY.
 */
export const formatDate = (parts: ICalendarDateParts): string => {
    const month = (parts.month + 1).toString().padStart(2, '0');
    const day = parts.day.toString().padStart(2, '0');
    return `${month}/${day}/${parts.year}`;
};

/**
 * Build store ISO range string: from 00:00:00 / to 23:59:59 in the given timezone.
 */
export const calendarRangeToIsoRangeString = (
    from: ICalendarDateParts,
    to: ICalendarDateParts,
    timeZone: string,
): string => {
    const fromIso = dateComponentsToUtcIso(from.year, from.month, from.day, 0, 0, 0, timeZone);
    const toIso = dateComponentsToUtcIso(to.year, to.month, to.day, 23, 59, 59, timeZone);
    return `${fromIso}/${toIso}`;
};

/**
 * Subtract calendar months, clamping the day when the target month is shorter
 * (e.g. 31 May minus 3 months → 28/29 Feb).
 */
export const subtractCalendarMonths = (
    parts: ICalendarDateParts,
    months: number,
): ICalendarDateParts => {
    const firstOfTarget = new Date(parts.year, parts.month - months, 1);
    const lastDayOfTarget = new Date(
        firstOfTarget.getFullYear(),
        firstOfTarget.getMonth() + 1,
        0,
    ).getDate();

    return {
        year: firstOfTarget.getFullYear(),
        month: firstOfTarget.getMonth(),
        day: Math.min(parts.day, lastDayOfTarget),
    };
};

/**
 * ISO range covering the last `months` calendar months ending today in `timeZone`
 * (start-of-day on that from date through end-of-day today).
 */
export const previousMonthsIsoRangeString = (
    months: number,
    timeZone: string,
    now: Date = new Date(),
): string => {
    const today = getCalendarDateInTimeZone(now.toISOString(), timeZone);
    if (!today) {
        return '';
    }

    return calendarRangeToIsoRangeString(subtractCalendarMonths(today, months), today, timeZone);
};

/**
 * Compare calendar dates (year/month/day only). Negative if a < b, 0 if equal, positive if a > b.
 */
export const compareCalendarDates = (a: ICalendarDateParts, b: ICalendarDateParts): number => {
    if (a.year !== b.year) {
        return a.year - b.year;
    }
    if (a.month !== b.month) {
        return a.month - b.month;
    }
    return a.day - b.day;
};

/**
 * Chip label day part, e.g. "Feb 8 2026" (no comma).
 */
export const formatChipCalendarDate = (parts: ICalendarDateParts): string => {
    const probe = new Date(parts.year, parts.month, parts.day);
    const formatted = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    }).format(probe);
    return formatted.replace(/,/g, '');
};

/**
 * Parse ISO date range string to IDateRangeISO object.
 * 
 * @param rangeString - Date range string in format "from/to" (e.g., "2025-01-01T00:00:00Z/2025-12-31T23:59:59Z")
 * @param requireIsoFormat - If true, validates that dates contain 'T' (ISO format). Defaults to false.
 * @returns IDateRangeISO object with from and to dates, or undefined if invalid
 * 
 * @example
 * parseIsoDateRange("2025-01-01T00:00:00Z/2025-12-31T23:59:59Z")
 * // Returns: { from: "2025-01-01T00:00:00Z", to: "2025-12-31T23:59:59Z" }
 * 
 * @remarks
 * This utility consolidates the common pattern of parsing date range strings
 * stored in format "from/to" into structured objects for DateRangePickerV2.
 */
export function parseIsoDateRange(
    rangeString: string,
    requireIsoFormat: boolean = false
): { from: string; to: string } | undefined {
    if (!rangeString || !rangeString.includes('/')) {
        return undefined;
    }

    if (requireIsoFormat && !rangeString.includes('T')) {
        return undefined;
    }

    const [from, to] = rangeString.split('/');
    if (!from || !to) {
        return undefined;
    }

    return { from, to };
}

/**
 * Parse a store ISO range string into calendar date parts in the given timezone.
 * Inverse of {@link calendarRangeToIsoRangeString}. Returns undefined when empty or invalid.
 */
export const isoRangeStringToCalendarRange = (
    rangeString: string,
    timeZone: string,
    requireIsoFormat: boolean = true,
): { from: ICalendarDateParts; to: ICalendarDateParts } | undefined => {
    const parsed = parseIsoDateRange(rangeString, requireIsoFormat);
    if (!parsed) {
        return undefined;
    }

    const from = getCalendarDateInTimeZone(parsed.from, timeZone);
    const to = getCalendarDateInTimeZone(parsed.to, timeZone);
    if (!from || !to) {
        return undefined;
    }

    return { from, to };
};

/**
 * Format ISO date range string for display with merchant timezone and format settings.
 * 
 * @param tzStore - TimezoneStore instance (can be null/undefined)
 * @param rangeString - Date range string in format "from/to" (e.g., "2025-01-01T00:00:00Z/2025-12-31T23:59:59Z")
 * @param dateTimeView - What parts to display (defaults to DATE_ONLY)
 * @param separator - Separator between dates (defaults to " - ")
 * @returns Formatted date range string (e.g., "01 Jan 2025 - 31 Dec 2025") or original string if invalid
 * 
 * @example
 * formatIsoDateRange(tzStore, "2025-01-01T00:00:00Z/2025-12-31T23:59:59Z")
 * // Returns: "01 Jan 2025 - 31 Dec 2025" (with merchant timezone/format)
 * 
 * @remarks
 * This utility consolidates the common pattern of formatting date range strings
 * for display in filter tags and other UI elements. It automatically handles
 * timezone conversion and format preferences.
 */
export function formatIsoDateRange(
    tzStore: TimezoneStore | null | undefined,
    rangeString: string,
    dateTimeView: DATETIME_VIEW = DATETIME_VIEW.DATE_ONLY,
    separator: string = ' - '
): string {
    const parsed = parseIsoDateRange(rangeString);
    if (!parsed) {
        return rangeString;
    }

    const fromFormatted = formatDateTimeWithFallback(tzStore, parsed.from, dateTimeView);
    const toFormatted = formatDateTimeWithFallback(tzStore, parsed.to, dateTimeView);

    return `${fromFormatted}${separator}${toFormatted}`;
}